// Buttons (design/DIRECTION.md, "Components: buttons"). One crimson primary per screen; everything
// else is secondary, ghost or text. The label rolls on hover (ui.module.css .roll), a large call to
// action can end in an arrow disc, and a primary can lean toward the pointer (Magnetic).
//
//   <Button variant="primary" size="lg" arrow>Join Fellowship</Button>
//   <LinkButton to="/plans" variant="secondary" icon="bible">Plans</LinkButton>
//   <IconButton label="Close" icon="close" />
import { createLink, type LinkComponent } from "@tanstack/react-router";
import { forwardRef, type AnchorHTMLAttributes, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Magnetic } from "../motion/Magnetic";
import { Icon, type IconName } from "./Icon";
import ui from "./ui.module.css";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "text" | "danger" | "inverse";
export type ButtonSize = "sm" | "md" | "lg";

type Look = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** A glyph before the label. */
  icon?: IconName;
  /** A glyph after the label. */
  iconEnd?: IconName;
  /** End with the arrow disc (large primary or inverse calls to action). */
  arrow?: boolean;
  /** Fill the width of its container. */
  wide?: boolean;
  /** Roll the label on hover. On by default for string labels. */
  roll?: boolean;
};

const VARIANT: Record<ButtonVariant, string> = {
  primary: ui.primary!,
  secondary: ui.secondary!,
  ghost: ui.ghost!,
  text: ui.text!,
  danger: ui.danger!,
  inverse: ui.inverse!,
};

/** The class list for a button look, for an element that can't be a Button (a <label>, a native <Link>). */
export function buttonClass({ variant = "secondary", size = "md", wide = false }: Pick<Look, "variant" | "size" | "wide"> = {}, extra?: string): string {
  return [VARIANT[variant], size === "sm" ? ui.sm : size === "lg" ? ui.lg : "", wide ? ui.wide : "", extra ?? ""].filter(Boolean).join(" ");
}

function Inner({ icon, iconEnd, arrow, roll, busy, size, children }: Look & { busy?: boolean; children?: ReactNode }) {
  const glyph = size === "sm" ? 16 : 18;
  const rolls = roll ?? typeof children === "string";
  return (
    <>
      {busy ? <span className={ui.spinner} aria-hidden="true" /> : icon && <Icon name={icon} size={glyph} />}
      {rolls ? (
        <span className={ui.roll}>
          <span className={ui.rollInner}>{children}</span>
        </span>
      ) : (
        children
      )}
      {iconEnd && <Icon name={iconEnd} size={glyph} />}
      {arrow && (
        <span className={ui.disc} aria-hidden="true">
          <Icon name="forward" size={18} />
        </span>
      )}
    </>
  );
}

export type ButtonProps = Look &
  ButtonHTMLAttributes<HTMLButtonElement> & {
    /** Working: shows a turning ring, keeps the width, and sets aria-busy. The label should say so too. */
    busy?: boolean;
    /** Lean toward the pointer (primary actions on large surfaces only). */
    magnetic?: boolean;
  };

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "secondary", size = "md", icon, iconEnd, arrow, wide, roll, busy, magnetic, className, children, type = "button", ...rest },
  ref,
) {
  const button = (
    <button ref={ref} type={type} className={buttonClass({ variant, size, wide }, className)} aria-busy={busy || undefined} {...rest}>
      <Inner icon={icon} iconEnd={iconEnd} arrow={arrow} roll={roll} busy={busy} size={size}>
        {children}
      </Inner>
    </button>
  );
  return magnetic ? <Magnetic>{button}</Magnetic> : button;
});

type AnchorLookProps = Look & AnchorHTMLAttributes<HTMLAnchorElement>;

const ButtonAnchor = forwardRef<HTMLAnchorElement, AnchorLookProps>(function ButtonAnchor(
  { variant = "secondary", size = "md", icon, iconEnd, arrow, wide, roll, className, children, ...rest },
  ref,
) {
  return (
    <a ref={ref} className={buttonClass({ variant, size, wide }, className)} {...rest}>
      <Inner icon={icon} iconEnd={iconEnd} arrow={arrow} roll={roll} size={size}>
        {children}
      </Inner>
    </a>
  );
});

/** A router link that looks like a button: every Link prop, plus the Button look. */
export const LinkButton: LinkComponent<typeof ButtonAnchor> = createLink(ButtonAnchor);

export type IconButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> & {
  /** The accessible name: what pressing it does ("Close", "More about Ruth"). */
  label: string;
  icon: IconName;
  size?: "sm" | "md" | "lg";
  /** ghost (default): a glyph that takes a tint; outline: a hairline ring; solid: crimson. */
  look?: "ghost" | "outline" | "solid";
  /** A toggle: sets aria-pressed and turns the glyph crimson. */
  pressed?: boolean;
  /** Show the label as a tooltip on hover and focus. */
  tip?: boolean;
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, icon, size = "md", look = "ghost", pressed, tip = false, className, type = "button", ...rest },
  ref,
) {
  const cls = [size === "sm" ? ui.iconSm : size === "lg" ? ui.iconLg : ui.icon, look === "outline" ? ui.iconOutline : look === "solid" ? ui.iconSolid : "", className ?? ""]
    .filter(Boolean)
    .join(" ");
  return (
    <button ref={ref} type={type} className={cls} aria-label={label} aria-pressed={pressed} data-tip={tip ? label : undefined} {...rest}>
      <Icon name={icon} size={size === "sm" ? 18 : size === "lg" ? 24 : 20} />
    </button>
  );
});
